/**
 * Sanitizes error messages to hide internal details from users
 * Returns user-friendly error messages instead
 */

const getUserFriendlyError = (error) => {
  // Handle different error formats
  const errorMessage = 
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    error?.toString() ||
    'An unexpected error occurred';

  // Check HTTP status codes FIRST - preserve backend messages for client errors
  const status = error?.response?.status;
  
  // For 401 (Unauthorized) and 400 (Bad Request), preserve the backend's error message
  // These are usually user-facing messages like "Invalid email or password"
  if (status === 401 || status === 400) {
    // Only sanitize if the message looks like an internal error
    if (
      errorMessage.includes('at ') ||
      errorMessage.includes('Error:') ||
      errorMessage.includes('Exception:') ||
      errorMessage.includes('\\') ||
      errorMessage.includes('/node_modules/') ||
      errorMessage.includes('stack trace') ||
      errorMessage.length > 200
    ) {
      // Message looks internal, sanitize it
    } else {
      // Return the backend's message as-is for user-facing errors
      return errorMessage;
    }
  }

  // Convert to lowercase for easier matching
  const lowerMessage = errorMessage.toLowerCase();

  // Map internal errors to user-friendly messages
  const errorMappings = {
    // Database errors
    'mongodb': 'Database error. Please try again.',
    'mongoose': 'Database error. Please try again.',
    'validation error': 'Invalid data provided. Please check your input.',
    'cast to objectid failed': 'Invalid data format. Please try again.',
    
    // Authentication errors (only for non-401 status codes)
    'unauthorized': 'Please log in to continue.',
    'invalid token': 'Your session has expired. Please log in again.',
    'token expired': 'Your session has expired. Please log in again.',
    'jwt': 'Authentication error. Please log in again.',
    
    // Network errors
    'network error': 'Network connection failed. Please check your internet connection.',
    'timeout': 'Request timed out. Please try again.',
    'econnrefused': 'Unable to connect to server. Please try again later.',
    
    // Server errors
    'internal server error': 'Server error. Please try again later.',
    '500': 'Server error. Please try again later.',
    '503': 'Service temporarily unavailable. Please try again later.',
    
    // Not found errors
    'not found': 'The requested resource was not found.',
    '404': 'The requested resource was not found.',
    
    // Permission errors
    'forbidden': 'You do not have permission to perform this action.',
    '403': 'You do not have permission to perform this action.',
    
    // Validation errors - only match specific validation patterns
    'field is required': 'Please fill in all required fields.',
    'is required': 'Please fill in all required fields.',
    'required field': 'Please fill in all required fields.',
    'invalid': 'Invalid input. Please check your data.',
    
    // Subscription errors
    'subscription': 'Subscription error. Please contact support.',
    'payment': 'Payment error. Please try again or contact support.',
    
    // Rate limiting
    'rate limit': 'Too many requests. Please wait a moment and try again.',
    'too many requests': 'Too many requests. Please wait a moment and try again.',
  };

  // Check for specific error patterns
  for (const [pattern, friendlyMessage] of Object.entries(errorMappings)) {
    if (lowerMessage.includes(pattern)) {
      return friendlyMessage;
    }
  }
  if (status) {
    switch (status) {
      case 400:
      case 401:
        // Already handled above - return backend message or fallback
        return errorMessage || (status === 400 ? 'Invalid request. Please check your input.' : 'Invalid email or password.');
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data. Please try again.';
      case 422:
        return 'Invalid data provided. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Service temporarily unavailable. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      case 504:
        return 'Request timed out. Please try again.';
      default:
        if (status >= 500) {
          return 'Server error. Please try again later.';
        }
        if (status >= 400) {
          return 'Request failed. Please try again.';
        }
    }
  }

  // If error message looks like an internal error (contains stack trace, file paths, etc.)
  if (
    errorMessage.includes('at ') ||
    errorMessage.includes('Error:') ||
    errorMessage.includes('Exception:') ||
    errorMessage.includes('\\') ||
    errorMessage.includes('/node_modules/') ||
    errorMessage.includes('stack trace')
  ) {
    return 'An unexpected error occurred. Please try again.';
  }

  // For known user-facing errors, return as-is (like validation messages)
  // But limit length to prevent showing long internal messages
  if (errorMessage.length > 200) {
    return 'An error occurred. Please try again.';
  }

  // Return a generic message for unknown errors
  return 'Something went wrong. Please try again.';
};

export default getUserFriendlyError;

